import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useState } from "react";

type CVScreenerAgentProps = {
  parsedContent: any;
};

export default function CVScreenerAgent({
  parsedContent,
}: CVScreenerAgentProps): JSX.Element {
  const [expandedDimensions, setExpandedDimensions] = useState<Record<number, boolean>>({});
  const [summaryOpen, setSummaryOpen] = useState(false);

  // Extract score report from payload
  const report = parsedContent;

  // Check for error responses
  if (report?.answer?.error || report?.error) {
    const errorMessage = report?.answer?.error || report?.error || "Something went wrong. Please try again.";

    return (
      <div style={{ padding: "16px", width: "100%" }}>
        <div
          style={{
            padding: "16px",
            backgroundColor: "#fffbeb",
            borderRadius: "8px",
            border: "1px solid #fbbf24",
            marginBottom: "16px",
          }}
        >
          <div style={{ fontSize: "15px", fontWeight: 600, color: "#92400e", marginBottom: "8px" }}>
            Heads Up
          </div>
          <div
            style={{
              fontSize: "13px",
              color: "#78350f",
              lineHeight: "1.6",
            }}
          >
            {errorMessage}
          </div>
        </div>
      </div>
    );
  }

  if (!report || !report.agent_name) {
    return (
      <div style={{ padding: "12px", color: "#666" }}>
        Processing CV screening results...
      </div>
    );
  }

  // Calculate overall match
  const weightedScore = report.weighted_score || 0;
  const disposition = report.disposition || "HOLD";

  // Dimension mapping - MUST MATCH backend config.py weights exactly
  const dimensions = [
    { key: "d1_jd_alignment", label: "JD Alignment & Capability Coverage", weight: 0.30 },
    { key: "d2_architecture", label: "Architecture Quality & Integration", weight: 0.20 },
    { key: "d3_performance", label: "Performance Validation & Benchmarks", weight: 0.20 },
    { key: "d4_deployment", label: "Deployment Maturity & Production Readiness", weight: 0.20 },
    { key: "d5_governance", label: "Governance, Security & Compliance", weight: 0.10 },
  ];

  // Log calculation breakdown for debugging (not shown in UI)
  React.useEffect(() => {
    if (report) {
      console.log("📊 CV Screener Score Calculation:");
      let calculatedTotal = 0;
      dimensions.forEach((dim) => {
        const dimData = report[dim.key];
        const score = dimData?.score || 0;
        const contribution = score * dim.weight;
        calculatedTotal += contribution;
        console.log(
          `  ${dim.label} (${(dim.weight * 100).toFixed(0)}%): ` +
          `${score.toFixed(2)} × ${dim.weight.toFixed(2)} = ${contribution.toFixed(2)}`
        );
      });
      console.log(`  ─────────────────────────────────────`);
      console.log(`  Calculated Total: ${calculatedTotal.toFixed(2)}`);
      console.log(`  Backend Total: ${weightedScore.toFixed(2)}`);
      if (Math.abs(calculatedTotal - weightedScore) > 0.01) {
        console.warn(`  ⚠️ MISMATCH: Calculated (${calculatedTotal.toFixed(2)}) !== Backend (${weightedScore.toFixed(2)})`);
      } else {
        console.log(`  ✅ Calculation matches backend`);
      }
    }
  }, [report]);

  // Disposition styling
  const getDispositionStyle = (disp: string) => {
    switch (disp) {
      case "ADVANCE":
        return { backgroundColor: "#10b981", color: "white" };
      case "HOLD":
        return { backgroundColor: "#f59e0b", color: "white" };
      case "REJECT":
        return { backgroundColor: "#ef4444", color: "white" };
      default:
        return { backgroundColor: "#6b7280", color: "white" };
    }
  };

  // Toggle dimension expansion
  const toggleDimension = (idx: number) => {
    setExpandedDimensions((prev) => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };

  // Convert numeric score to qualitative label
  const getScoreLabel = (score: number): string => {
    if (score >= 3.0) return "High";
    if (score >= 2.0) return "Medium";
    return "Low";
  };

  // Get overall score label for weighted score
  const getOverallScoreLabel = (weightedScore: number): string => {
    if (weightedScore >= 3.0) return "High";
    if (weightedScore >= 2.0) return "Medium";
    return "Low";
  };

  // Export to PDF (simple approach - opens print dialog)
  const exportToPDF = () => {
    window.print();
  };

  return (
    <div style={{ width: "100%", color: "#333" }}>
      {/* Overall Score Card */}
      <div
        style={{
          padding: "16px",
          backgroundColor: "#f0f9ff",
          borderRadius: "8px",
          border: "1px solid #bae6fd",
          marginBottom: "16px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
          <div>
            <div style={{ fontSize: "14px", color: "#0369a1", marginBottom: "4px" }}>
              <strong>{report.agent_name}</strong>
            </div>
            <div style={{ fontSize: "12px", color: "#0c4a6e" }}>
              Job: {report.jd_id}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "24px", fontWeight: 600, color: "#0369a1" }}>
              {getOverallScoreLabel(weightedScore)}
            </div>
            <div style={{ fontSize: "10px", color: "#64748b", marginTop: "2px" }}>
              Overall Match
            </div>
          </div>
        </div>
      </div>

      {/* Dimension Breakdown - Individual Cards */}
      <div style={{ marginBottom: "16px" }}>
        <div style={{ fontWeight: 600, fontSize: "14px", marginBottom: "12px", color: "#374151" }}>
          Dimension Breakdown
        </div>

        {dimensions.map((dim, idx) => {
          const dimData = report[dim.key];
          const score = dimData?.score || 0;
          const confidence = dimData?.confidence || "N/A";
          const citation = dimData?.citation || "";
          const missing = dimData?.missing || [];
          const isExpanded = expandedDimensions[idx] || false;
          const weightPercent = (dim.weight * 100).toFixed(0);

          // Score color
          const scoreColor =
            score >= 3 ? "#10b981" : score >= 2 ? "#f59e0b" : score > 0 ? "#ef4444" : "#9ca3af";

          return (
            <div
              key={idx}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                marginBottom: "8px",
                overflow: "hidden",
              }}
            >
              {/* Dimension Header */}
              <div
                style={{
                  padding: "12px",
                  backgroundColor: "#f9fafb",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
                onClick={() => toggleDimension(idx)}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {isExpanded ? (
                      <ExpandLessIcon style={{ fontSize: "20px", color: "#6b7280" }} />
                    ) : (
                      <ExpandMoreIcon style={{ fontSize: "20px", color: "#6b7280" }} />
                    )}
                    <span style={{ fontWeight: 600, fontSize: "13px", color: "#111827" }}>
                      {dim.label}
                    </span>
                  </div>
                </div>

                {/* Score Label */}
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: 600,
                    color: scoreColor,
                    minWidth: "60px",
                    textAlign: "right",
                  }}
                >
                  {dimData ? getScoreLabel(score) : "N/A"}
                </div>
              </div>

              {/* Dimension Details (Expandable) */}
              {isExpanded && (
                <div style={{ padding: "12px", backgroundColor: "#ffffff" }}>
                  {dimData ? (
                    <>
                      {/* Citation */}
                      {citation && (
                        <div style={{ marginBottom: "12px" }}>
                          <div style={{ fontSize: "11px", fontWeight: 600, color: "#374151", marginBottom: "4px" }}>
                            Evidence:
                          </div>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#4b5563",
                              backgroundColor: "#f9fafb",
                              padding: "8px",
                              borderRadius: "4px",
                              lineHeight: "1.6",
                            }}
                          >
                            {citation}
                          </div>
                        </div>
                      )}

                      {/* Missing Items */}
                      {missing.length > 0 && (
                        <div>
                          <div style={{ fontSize: "11px", fontWeight: 600, color: "#991b1b", marginBottom: "4px" }}>
                            Missing:
                          </div>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#dc2626",
                              backgroundColor: "#fef2f2",
                              padding: "8px",
                              borderRadius: "4px",
                              lineHeight: "1.6",
                            }}
                          >
                            {missing.join(", ")}
                          </div>
                        </div>
                      )}

                      {!citation && missing.length === 0 && (
                        <div style={{ fontSize: "11px", color: "#9ca3af", fontStyle: "italic" }}>
                          No additional details available
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ fontSize: "11px", color: "#9ca3af", fontStyle: "italic" }}>
                      This dimension was not evaluated
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Green Flags */}
      {report.green_flags && report.green_flags.length > 0 && (
        <div style={{ marginBottom: "12px", padding: "12px", backgroundColor: "#f0fdf4", borderRadius: "8px", border: "1px solid #bbf7d0" }}>
          <div style={{ fontSize: "12px", fontWeight: 600, color: "#166534", marginBottom: "8px" }}>
            ✅ Green Flags
          </div>
          {report.green_flags.map((flag: string, i: number) => (
            <div key={i} style={{ fontSize: "12px", color: "#14532d", lineHeight: "1.8" }}>
              ✓ {flag}
            </div>
          ))}
        </div>
      )}

      {/* Red Flags */}
      {report.red_flags && report.red_flags.length > 0 && (
        <div style={{ marginBottom: "12px", padding: "12px", backgroundColor: "#fffbeb", borderRadius: "8px", border: "1px solid #fde68a" }}>
          <div style={{ fontSize: "12px", fontWeight: 600, color: "#92400e", marginBottom: "8px" }}>
            ⚠️ Red Flags
          </div>
          {report.red_flags.map((flag: string, i: number) => (
            <div key={i} style={{ fontSize: "12px", color: "#78350f", lineHeight: "1.8" }}>
              ⚠ {flag}
            </div>
          ))}
        </div>
      )}

      {/* Critical Red Flags */}
      {report.critical_red_flags && report.critical_red_flags.length > 0 && (
        <div style={{ marginBottom: "12px", padding: "12px", backgroundColor: "#fef2f2", borderRadius: "8px", border: "2px solid #ef4444" }}>
          <div style={{ fontSize: "12px", fontWeight: 600, color: "#991b1b", marginBottom: "8px" }}>
            ❌ Critical Red Flags
          </div>
          {report.critical_red_flags.map((flag: string, i: number) => (
            <div key={i} style={{ fontSize: "12px", color: "#7f1d1d", lineHeight: "1.8" }}>
              ✗ {flag}
            </div>
          ))}
        </div>
      )}

      {/* JD Coverage */}
      {report.jd_coverage && (
        <div style={{ marginBottom: "12px", padding: "12px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <div style={{ fontSize: "12px", fontWeight: 600, color: "#334155" }}>
              JD Coverage
            </div>
            <div style={{ fontSize: "14px", fontWeight: 600, color: "#0369a1" }}>
              {report.jd_coverage.coverage_percentage ?? "--"}%
            </div>
          </div>
          {report.jd_coverage.requirements_met?.length > 0 && (
            <div style={{ marginBottom: "6px" }}>
              <div style={{ fontSize: "11px", fontWeight: 600, color: "#166534", marginBottom: "2px" }}>Met:</div>
              {report.jd_coverage.requirements_met.map((req: string, i: number) => (
                <div key={i} style={{ fontSize: "11px", color: "#14532d", lineHeight: "1.6" }}>✓ {req}</div>
              ))}
            </div>
          )}
          {report.jd_coverage.requirements_missing?.length > 0 && (
            <div>
              <div style={{ fontSize: "11px", fontWeight: 600, color: "#991b1b", marginBottom: "2px" }}>Missing:</div>
              {report.jd_coverage.requirements_missing.map((req: string, i: number) => (
                <div key={i} style={{ fontSize: "11px", color: "#dc2626", lineHeight: "1.6" }}>✗ {req}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Summary (Expandable) */}
      {report.summary && (
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          {/* Summary Header */}
          <div
            style={{
              padding: "12px",
              backgroundColor: "#f9fafb",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
            onClick={() => setSummaryOpen(!summaryOpen)}
          >
            {summaryOpen ? (
              <ExpandLessIcon style={{ fontSize: "20px", color: "#6b7280" }} />
            ) : (
              <ExpandMoreIcon style={{ fontSize: "20px", color: "#6b7280" }} />
            )}
            <span style={{ fontWeight: 600, fontSize: "13px", color: "#111827" }}>
              Overall Summary
            </span>
          </div>

          {/* Summary Content */}
          {summaryOpen && (
            <div
              style={{
                padding: "12px",
                backgroundColor: "#ffffff",
                fontSize: "12px",
                color: "#4b5563",
                lineHeight: "1.7",
              }}
            >
              {report.summary}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
